/**
 * Build-time configuration, frozen at module load.
 *
 * Every VITE_* value is inlined by `vite build`. There is no runtime config fetch, by
 * choice: a config round-trip before first paint would break the "instant" rendering tier.
 * The consequence is that CI builds ONCE PER ENVIRONMENT — a single artifact promoted
 * across environments would carry the wrong Firebase project and the wrong Edge URL, and
 * would authenticate real users against a dev IdP.
 *
 * None of these values is a secret. Firebase Web API keys are identifiers, not credentials;
 * the Edge base URL is a public HTTPS endpoint. What protects them is GCIP's domain
 * allow-list and the Edge validating every token and re-deriving scope on every call.
 *
 * There is deliberately no Stripe variable of any kind here: the portal mounts no Stripe.js,
 * has no card form, and never tokenises. Checkout and the billing portal are server-created
 * redirect URLs. A Stripe key appearing in this file would mean a design decision had been
 * reversed, not that a config entry was missing — and security.test.ts greps this file to
 * make sure it stays that way, which is why the variable prefix is described rather than
 * written out.
 */

export type PgEnv = "development" | "staging" | "production";

export interface AppConfig {
  readonly env: PgEnv;
  readonly edgeBaseUrl: string;
  readonly portalOrigin: string;
  readonly siteOrigin: string;
  readonly firebase: {
    readonly apiKey: string;
    readonly authDomain: string;
    readonly projectId: string;
  };
}

type Env = Record<string, string | undefined>;

function require_(env: Env, name: string): string {
  const v = env[name];
  if (typeof v !== "string" || v.trim() === "") {
    throw new ConfigError(name, "is missing");
  }
  return v.trim();
}

function requireUrl(env: Env, name: string): string {
  const raw = require_(env, name);
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw new ConfigError(name, `is not a URL: ${raw}`);
  }
  // http is allowed only for a local dev origin; anything shipped is https.
  const localhost = url.hostname === "localhost" || url.hostname === "127.0.0.1";
  if (url.protocol !== "https:" && !(url.protocol === "http:" && localhost)) {
    throw new ConfigError(name, `must be https (got ${url.protocol})`);
  }
  return raw.replace(/\/+$/, "");
}

function requireEnum<T extends string>(env: Env, name: string, allowed: readonly T[]): T {
  const v = require_(env, name);
  if (!(allowed as readonly string[]).includes(v)) {
    throw new ConfigError(name, `must be one of ${allowed.join(" | ")} (got ${v})`);
  }
  return v as T;
}

export class ConfigError extends Error {
  constructor(name: string, problem: string) {
    super(
      `Configuration error: ${name} ${problem}. ` +
        `The portal refuses to start rather than fail on its first request — see docs/ENV_AND_SECRETS.md §3.`,
    );
    this.name = "ConfigError";
  }
}

/** Exported for tests; the app uses the frozen singleton below. */
export function readConfig(env: Env): AppConfig {
  return Object.freeze({
    env: requireEnum(env, "VITE_PG_ENV", ["development", "staging", "production"] as const),
    edgeBaseUrl: requireUrl(env, "VITE_EDGE_BASE_URL"),
    portalOrigin: requireUrl(env, "VITE_PORTAL_ORIGIN"),
    siteOrigin: requireUrl(env, "VITE_SITE_ORIGIN"),
    firebase: Object.freeze({
      apiKey: require_(env, "VITE_FIREBASE_API_KEY"),
      authDomain: require_(env, "VITE_FIREBASE_AUTH_DOMAIN"),
      projectId: require_(env, "VITE_FIREBASE_PROJECT_ID"),
    }),
  });
}

/**
 * Under test, `import.meta.env` carries Vite's defaults but none of ours, and a thrown
 * ConfigError at module load would take down every unit test. Tests exercise `readConfig`
 * directly (src/test/config.test.ts) including its negative cases.
 */
const rawEnv = import.meta.env as unknown as Env;

export const config: AppConfig = rawEnv.MODE === "test" || rawEnv.VITEST
  ? {
      env: "development",
      edgeBaseUrl: "https://edge.test",
      portalOrigin: "https://app.test",
      siteOrigin: "https://site.test",
      firebase: { apiKey: "test", authDomain: "test.firebaseapp.com", projectId: "test" },
    }
  : readConfig(rawEnv);

/** The public Chrome Web Store listing. Not environment-specific, so a constant, not a VITE_* value. */
export const CHROME_WEB_STORE_URL =
  "https://chromewebstore.google.com/detail/lnllnjkfagkcecgiahaodpldnecpbopl";
